<script setup>
import { onMounted, onUnmounted, shallowRef, markRaw } from 'vue'
import { useStore } from 'vuex'

import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css'
import MapboxDraw from '@mapbox/mapbox-gl-draw'
import { bringDrawLayersToTop } from '@/composables/useZLayers'
import { DRAW_STYLES } from '@/utils/mapbox/drawStyles'
import DrawCircleMode from '@/utils/mapbox/drawCircleMode'

const store = useStore()
const props = defineProps({
  map: {
    type: Object,
    required: true
  },
  // The single global draw lives in geo/SET_DRAW (project view reads it). A
  // split tile does not commit its own draw there — MapGrid publishes the
  // first visible tile's draw via the `ready` emit instead.
  global: {
    type: Boolean,
    default: true
  }
})

const emit = defineEmits(['ready'])

const drawInstance = shallowRef(null)

// mapbox-gl-draw adds its sources once, on map load, and never re-adds them
// after a setStyle() (basemap switching) wipes them — its render() then bails
// and drawing goes dead. Re-attaching rebuilds them; onRemove drops the store,
// so the features are carried over by hand.
const reattachDraw = () => {
  const instance = drawInstance.value
  if (!props.map || !instance || props.map.getSource('mapbox-gl-draw-hot')) return
  const features = instance.getAll()
  props.map.removeControl(instance)
  props.map.addControl(instance, 'bottom-right')
  if (features.features.length) instance.set(features)
}

const raiseDraw = () => {
  reattachDraw()
  bringDrawLayersToTop(props.map)
}

onUnmounted(() => {
  if (props.map) props.map.off('style.load', raiseDraw)
  if (props.global && store.getters['geo/getDraw'] === drawInstance.value) {
    store.commit('geo/SET_DRAW', null)
  }
})

onMounted(() => {
  const instance = markRaw(new MapboxDraw({
    displayControlsDefault: false,
    userProperties: true,
    styles: DRAW_STYLES,
    modes: { ...MapboxDraw.modes, draw_circle: DrawCircleMode }
  }))

  props.map.addControl(instance, 'bottom-right')
  drawInstance.value = instance
  // Only the global canvas publishes its draw to geo — tiles keep their own.
  if (props.global) store.commit('geo/SET_DRAW', instance)
  bringDrawLayersToTop(props.map)
  props.map.on('style.load', raiseDraw)
  emit('ready', instance)
})
</script>

<template>
  <slot :draw="drawInstance"></slot>
</template>

<style>
/* Layer hover handlers write an inline cursor on the canvas, which beats mapbox-gl-draw's
   crosshair (set on the canvas container). While a draw mode is active, the crosshair wins. */
.mapboxgl-map.mouse-add .mapboxgl-canvas-container.mapboxgl-interactive,
.mapboxgl-map.mouse-add .mapboxgl-canvas-container.mapboxgl-interactive:active,
.mapboxgl-map.mouse-add .mapboxgl-canvas {
  cursor: crosshair !important;
}
</style>
